import "./App.css";
import "./assets/css/base.css";

import React, { useEffect, useState, useMemo } from "react";

import { apiInitSetting } from "./api";
import { generateUUID, isEmpty } from "./utils";

import viVN from "antd/locale/vi_VN";
import enUS from "antd/locale/en_US";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";
import { useSettingStore, useScanQrcodeStore } from "@/store";

import { ConfigProvider, Spin } from "antd";

import HomeView from "./views/HomeView";
import KichHoatView from "./views/KichHoatView";
import OldPisenView from "./views/OldPisenView";
import OldMirascanView from "./views/OldMirascanView";
import BusinessView from "./views/BusinessView";
import NotFoundView from "./views/NotFoundView";
import ErrorView from "./views/ErrorView";
import StampScanErrorView from "./views/StampScanErrorView";
import IframeView from "./views/IframeView";
import { LoadingOutlined } from "@ant-design/icons";

const useDocumentTitle = (title: string) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
};

const useFavicon = (url: string) => {
  useEffect(() => {
    const link = (document.querySelector("link[rel*='icon']") as HTMLLinkElement) || document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = url;
    document.getElementsByTagName("head")[0].appendChild(link);
  }, [url]);
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { t, i18n } = useTranslation();

  // Store hooks
  const store = useScanQrcodeStore();
  const storeSetting = useSettingStore();

  // Computed values
  const companyName = useMemo(() => store?.company?.name || "", [store?.company?.name]);
  const companyUrl = useMemo(() => store?.company?.small_light_logo_url || "", [store?.company?.small_light_logo_url]);
  const langKey = useMemo(() => store?.company?.lang_key || "vi", [store?.company?.lang_key]);

  const textDefault = useMemo(() => store.getKeyThemeData("text_default") || "#4B6166", [store]);
  const bgWarning = useMemo(() => store.getKeyThemeData("color_warring") || "#F38020", [store]);
  const colorError = useMemo(() => store.getKeyThemeData("color_error") || "#B61212", [store]);
  const colorSuccess = useMemo(() => store.getKeyThemeData("color_success") || "#00994D", [store]);

  // Set document title
  useDocumentTitle(companyName ? `Xác thực và Bảo hành online - ${companyName}` : "Xác thực và Bảo hành online");

  // Set favicon
  useFavicon(!isEmpty(companyUrl) ? companyUrl : "/src/assets/images/logo.png");

  // Generate browser ID
  useEffect(() => {
    const browserId = localStorage.getItem("browser_id");
    if (!browserId) {
      localStorage.setItem("browser_id", generateUUID() as string);
    }
  }, []);

  // Handle language change based on company lang_key
  useEffect(() => {
    if (langKey && i18n.language !== langKey) {
      i18n.changeLanguage(langKey);
      localStorage.setItem("i18nextLng", langKey);
    }
  }, [langKey, i18n]);

  // Compute antd locale based on current language
  const antdLocale = useMemo(() => {
    return i18n.language === "en" ? enUS : viVN;
  }, [i18n.language]);

  // Init data function
  const initData = async () => {
    if (storeSetting.isFetchInit) {
      return;
    }

    try {
      const response = await apiInitSetting();
      const { data: dataResponse } = response.data;
      storeSetting.setSettingData(dataResponse?.credentials || {});
      storeSetting.actionIsFetched();
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  // Component did mount
  useEffect(() => {
    initData();
  }, []);

  // Dynamic styles object
  // const dynamicStyles = useMemo(
  //   () => ({
  //     "--color-success": colorSuccess,
  //     "--color-warning": bgWarning,
  //     "--color-error": colorError,
  //     "--text-default": textDefault,
  //   }),
  //   [colorSuccess, bgWarning, colorError, textDefault],
  // );

  return (
    <div
      className="app-container"
      style={
        {
          "--color-success": colorSuccess,
          "--color-warning": bgWarning,
          "--color-error": colorError,
          "--text-default": textDefault,
        } as React.CSSProperties
      }
    >
      <ConfigProvider
        locale={antdLocale}
        theme={{
          token: {
            colorPrimary: colorSuccess,
            colorInfo: colorSuccess,
            colorLink: colorSuccess,
            borderRadius: 10,
            fontFamily: '"Be Vietnam Pro", sans-serif',
          },
        }}
      >
        {isLoading ? (
          <div className="loading-overlay flex flex-col items-center justify-center gap-2">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}/>
            <div className="loading-text">{t("common.loading")}</div>
          </div>
        ) : (
          <div className="qrx-container !m-auto">
            <Routes>
              <Route path="/xac-thuc" element={<HomeView />} />
              <Route path="/" element={<HomeView />} />
              <Route path="/kichhoat" element={<KichHoatView />} />
              <Route path="/series/qrcode" element={<OldPisenView />} />
              <Route path="/v/s" element={<OldMirascanView />} />
              <Route path="/p/#/v/p" element={<OldMirascanView />} />
              <Route path="/noi-bo" element={<BusinessView />} />
              <Route path="/not-found" element={<NotFoundView />} />
              <Route path="/error" element={<ErrorView />} />
              <Route path="/stamp-error" element={<StampScanErrorView />} />
              <Route path="/iframe" element={<IframeView />} />
              <Route path="*" element={<NotFoundView />} />
            </Routes>
          </div>
        )}
      </ConfigProvider>
    </div>
  );
};

export default App;
